import React, { useState, useEffect, useMemo } from 'react';
import { Tag, Trash2, Check, Copy, Briefcase, Percent, CheckCheck, ShieldCheck, Send } from 'lucide-react';
import { getBuckets, getCategories } from '../../lib/api';
import { groupByBucket } from '../../lib/categoryGrouping';
import TaxCodePicker from '../TaxCodePicker';

/**
 * Phase 19 — Bulk actions bar. Phase 20 replaces the legacy rule-key
 * dropdown with the province-scoped `TaxCodePicker` modal. Phase 25.3
 * adds the "Publish to QB" trigger (shown in both contexts).
 *
 * `context` controls which extra actions are shown:
 *   - `receipts`   (default) — no Bookkeeper-specific actions
 *   - `bookkeeper` — adds "Mark as Reviewed" + "Mark Tax Reviewed"
 */
export default function BulkActionsBar({
  onCategorize,
  onApprove,
  onDelete,
  onMarkDuplicate,
  onClassifyAsset,
  onTaxCode,          // legacy — writes tax_rule_key_override (still supported)
  onTaxCodeV2,        // Phase 20 — writes tax_code_override
  onMarkReviewed,     // Bookkeeper Mode
  onMarkTaxReviewed,  // Bookkeeper Mode
  onPublishToQb,      // Phase 25.3 — triggers BulkPublishModal
  context = 'receipts',
  colors,
  countLabel = '',
}) {
  const [catsOpen, setCatsOpen] = useState(false);
  const [assetOpen, setAssetOpen] = useState(false);
  const [taxOpen, setTaxOpen] = useState(false);
  const [cats, setCats] = useState([]);
  const [buckets, setBuckets] = useState([]);

  useEffect(() => {
    if (!catsOpen || cats.length > 0) return;
    Promise.all([
      getCategories().then((r) => r.categories.filter((c) => !c.is_special)),
      getBuckets().catch(() => []),
    ])
      .then(([list, bs]) => { setCats(list); setBuckets(bs); })
      .catch((err) => console.error('Failed to load categories for bulk bar:', err));
  }, [catsOpen, cats.length]);

  const groupedCats = useMemo(() => groupByBucket(cats, buckets), [cats, buckets]);

  const closeAll = () => { setCatsOpen(false); setAssetOpen(false); setTaxOpen(false); };

  const btn = (extra = {}) => ({
    height: 40, borderRadius: 999,
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    padding: '0 14px', fontSize: 13, fontWeight: 600,
    border: `1px solid ${colors.border}`, backgroundColor: colors.bg, color: colors.text,
    cursor: 'pointer', whiteSpace: 'nowrap', ...extra,
  });

  return (
    <div
      className="flex flex-wrap items-center gap-2 px-4 sm:px-6 lg:px-8 py-3 border-t relative"
      style={{ backgroundColor: colors.surface, borderColor: colors.border }}
      data-testid="bulk-bar"
    >
      {/* Delete */}
      <button
        data-testid="bulk-delete"
        onClick={() => { closeAll(); onDelete && onDelete(); }}
        style={btn({ color: colors.error })}
      >
        <Trash2 size={14} /> Delete
      </button>

      {/* Category picker */}
      <div className="relative">
        <button
          data-testid="bulk-category-btn"
          onClick={() => { setCatsOpen((v) => !v); setAssetOpen(false); }}
          style={btn()}
        >
          <Tag size={14} /> Categorize
        </button>
        {catsOpen && (
          <div
            className="absolute bottom-12 left-0 max-h-[300px] w-72 overflow-y-auto rounded-xl border shadow-lg z-30"
            style={{ backgroundColor: colors.surface, borderColor: colors.border }}
            data-testid="bulk-category-menu"
          >
            {groupedCats.map((row) => (
              <div key={row.bucket}>
                <div className="flex items-center gap-1.5 px-4 pt-2 pb-1 sticky top-0"
                  style={{ backgroundColor: colors.surface }}
                >
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: row.color }} />
                  <span className="text-[10px] font-bold uppercase tracking-wider"
                    style={{ color: colors.textMuted }}
                  >{row.label}</span>
                </div>
                {row.categories.map((c) => (
                  <button
                    key={c.id}
                    data-testid={`bulk-cat-opt-${c.name}`}
                    onClick={() => { setCatsOpen(false); onCategorize && onCategorize(c.name); }}
                    className="w-full flex items-center justify-between px-4 py-1.5 text-sm hover:opacity-80 text-left"
                    style={{ color: colors.text }}
                  >
                    <span>{c.name}</span>
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Mark duplicate */}
      <button
        data-testid="bulk-mark-duplicate"
        onClick={() => { closeAll(); onMarkDuplicate && onMarkDuplicate(); }}
        style={btn()}
      >
        <Copy size={14} /> Mark duplicate
      </button>

      {/* Classify asset / expense */}
      <div className="relative">
        <button
          data-testid="bulk-asset-btn"
          onClick={() => { setAssetOpen((v) => !v); setCatsOpen(false); }}
          style={btn()}
        >
          <Briefcase size={14} /> Asset/Expense
        </button>
        {assetOpen && (
          <div
            className="absolute bottom-12 left-0 rounded-xl border shadow-lg z-30 min-w-[180px]"
            style={{ backgroundColor: colors.surface, borderColor: colors.border }}
            data-testid="bulk-asset-menu"
          >
            <button
              data-testid="bulk-asset-classify-asset"
              onClick={() => { setAssetOpen(false); onClassifyAsset && onClassifyAsset(true); }}
              className="w-full text-left px-4 py-2 text-sm hover:opacity-80"
              style={{ color: colors.text }}
            >
              Classify as asset
            </button>
            <button
              data-testid="bulk-asset-classify-expense"
              onClick={() => { setAssetOpen(false); onClassifyAsset && onClassifyAsset(false); }}
              className="w-full text-left px-4 py-2 text-sm hover:opacity-80"
              style={{ color: colors.text, borderTop: `1px solid ${colors.border}` }}
            >
              Classify as expense
            </button>
          </div>
        )}
      </div>

      {/* Tax code — Phase 20 picker */}
      <button
        data-testid="bulk-tax-btn"
        onClick={() => { closeAll(); setTaxOpen(true); }}
        style={btn()}
      >
        <Percent size={14} /> Tax code
      </button>

      {/* Bookkeeper Mode — Mark as Reviewed + Mark Tax Reviewed */}
      {context === 'bookkeeper' && (
        <>
          <button
            data-testid="bulk-mark-reviewed"
            onClick={() => { closeAll(); onMarkReviewed && onMarkReviewed(); }}
            style={btn()}
          >
            <CheckCheck size={14} /> Mark as Reviewed
          </button>
          <button
            data-testid="bulk-mark-tax-reviewed"
            onClick={() => { closeAll(); onMarkTaxReviewed && onMarkTaxReviewed(); }}
            style={btn()}
          >
            <ShieldCheck size={14} /> Mark Tax Reviewed
          </button>
        </>
      )}

      {/* Approve — primary, pushed right */}
      <button
        data-testid="bulk-approve"
        onClick={() => { closeAll(); onApprove && onApprove(); }}
        className="ml-auto"
        style={btn({ backgroundColor: colors.accent, color: '#FFFFFF', border: `1px solid ${colors.accent}`, padding: '0 20px' })}
      >
        <Check size={14} /> Approve
      </button>

      {/* Phase 25.3 — Publish to QuickBooks (shown in all contexts) */}
      {onPublishToQb && (
        <button
          data-testid="bulk-publish-qb"
          onClick={() => { closeAll(); onPublishToQb(); }}
          style={btn({ backgroundColor: '#2CA01C', color: '#FFFFFF', border: '1px solid #2CA01C', padding: '0 18px' })}
        >
          <Send size={14} /> Publish to QB
        </button>
      )}

      {/* Tax code picker modal */}
      <TaxCodePicker
        open={taxOpen}
        titleSuffix={countLabel ? `· ${countLabel}` : ''}
        onCancel={() => setTaxOpen(false)}
        onClear={() => {
          setTaxOpen(false);
          if (onTaxCodeV2) {
            onTaxCodeV2({ federal_code: null, provincial_code: null, no_tax_applicable: false });
          } else if (onTaxCode) {
            onTaxCode(null);
          }
        }}
        onApply={(tc) => {
          setTaxOpen(false);
          if (onTaxCodeV2) onTaxCodeV2(tc);
        }}
      />
    </div>
  );
}
